import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../../context/AuthContext';
import { formatPhoneNumber, validateSouthAfricanPhone } from '../../utils/helpers';
import './UserProfile.css';

const UserProfile = ({ onClose }) => {
  const { user } = useAuth();
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const [formData, setFormData] = useState({
    name: user?.name || '',
    phone: user?.phone || '',
    home_address: user?.home_address || ''
  });
  
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswordForm, setShowPasswordForm] = useState(false);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

  // Prevent body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  const handleClose = () => {
    document.body.style.overflow = 'unset';
    onClose();
  };

  const handleCancel = () => {
    setEditing(false);
    setFormData({ name: user?.name || '', phone: user?.phone || '', home_address: user?.home_address || '' });
    setError('');
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const handlePasswordChange = (e) => {
    setPasswordData({
      ...passwordData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Validate inputs
      if (!formData.name.trim()) {
        setError('Name is required');
        return;
      }
      
      if (formData.phone && !validateSouthAfricanPhone(formData.phone)) {
        setError('Please enter a valid South African phone number');
        return;
      }

      const response = await fetch(`${BACKEND_URL}/api/auth/profile?user_id=${user.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({
          name: formData.name,
          phone: formData.phone
        })
      });

      if (response.ok) {
        const updatedUser = await response.json();
        
        // Update localStorage and trigger auth context update
        const currentUser = JSON.parse(localStorage.getItem('user'));
        const newUserData = { ...currentUser, ...updatedUser };
        localStorage.setItem('user', JSON.stringify(newUserData));
        
        // Dispatch custom event to update auth context
        window.dispatchEvent(new CustomEvent('userLoggedIn', { 
          detail: { user: newUserData } 
        }));
        
        setSuccess('Profile updated successfully');
        setEditing(false);
        setTimeout(() => setSuccess(''), 3000);
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to update profile');
      }
    } catch (error) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Validate password inputs
      if (!passwordData.currentPassword) {
        setError('Current password is required');
        return;
      }
      
      if (!passwordData.newPassword || passwordData.newPassword.length < 6) {
        setError('New password must be at least 6 characters');
        return;
      }
      
      if (passwordData.newPassword !== passwordData.confirmPassword) {
        setError('New passwords do not match');
        return;
      }

      const response = await fetch(`${BACKEND_URL}/api/auth/change-password?user_id=${user.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({
          currentPassword: passwordData.currentPassword,
          newPassword: passwordData.newPassword
        })
      });

      if (response.ok) {
        setSuccess('Password changed successfully');
        setShowPasswordForm(false);
        setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
        setTimeout(() => setSuccess(''), 3000);
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to change password');
      }
    } catch (error) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getUserTypeIcon = () => {
    switch (user?.userType) {
      case 'rider': return '👤';
      case 'driver': return '🚗';
      case 'admin': return '🛠️';
      default: return '👤';
    }
  };

  const getUserTypeDisplay = () => {
    switch (user?.userType) {
      case 'rider': return 'Customer';
      case 'driver': return 'Driver';
      case 'admin': return 'Administrator';
      default: return 'User';
    }
  };

  return createPortal(
    <div className="profile-modal-overlay" onClick={handleClose}>
      <div className="profile-modal" onClick={(e) => e.stopPropagation()}>
        <div className="login-header">
          <h2>My Profile</h2>
          <button className="close-btn" onClick={handleClose}>✕</button>
        </div>

        <div className="profile-content">
          {error && <div className="error-message">{error}</div>}
          {success && <div className="info-message">{success}</div>}
          
          {/* User Avatar and Type */}
          <div className="profile-avatar">
            <div className="avatar-circle">
              {getUserTypeIcon()}
            </div>
            <div className="profile-type-badge">
              {getUserTypeDisplay()}
            </div>
          </div>

          {/* Conditional Rendering for Edit Mode vs Display Mode */}
          {editing ? (
            <div className="profile-edit">
              <div className="input-group">
                <input
                  type="text"
                  name="name"
                  placeholder="Full Name"
                  value={formData.name}
                  onChange={handleInputChange}
                  style={{ marginBottom: '1rem' }}
                />
              </div>
              <div className="input-group">
                <input
                  type="tel"
                  name="phone"
                  placeholder="Phone Number (+27...)"
                  value={formData.phone}
                  onChange={handleInputChange}
                />
              </div>
              {user?.userType === 'rider' && (
                <div className="input-group" style={{ marginTop: '1rem' }}>
                  <input
                    type="text"
                    name="home_address"
                    placeholder="Home Address (optional)"
                    value={formData.home_address}
                    onChange={handleInputChange}
                    data-testid="home-address-input"
                  />
                  <small style={{ color: '#666', fontSize: '0.75rem', marginTop: '0.25rem', display: 'block' }}>
                    Save your home address for quick booking
                  </small>
                </div>
              )}
              <div style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem' }}>
                <button 
                  type="button"
                  className="btn-outline"
                  onClick={handleCancel}
                  disabled={loading}
                  style={{ flex: 1 }}
                >
                  Cancel
                </button>
                <button 
                  type="button"
                  className="login-submit-btn"
                  onClick={handleSave}
                  disabled={loading}
                  style={{ flex: 1 }}
                >
                  {loading ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="profile-details">
                <div className="profile-detail-item">
                  <span className="profile-detail-label">Name</span>
                  <span className="profile-detail-value">{user?.name}</span>
                </div>
                <div className="profile-detail-item">
                  <span className="profile-detail-label">Email</span>
                  <span className="profile-detail-value">{user?.email}</span>
                </div>
                <div className="profile-detail-item">
                  <span className="profile-detail-label">Phone</span>
                  <span className="profile-detail-value">{formatPhoneNumber(user?.phone)}</span>
                </div>
                {user?.userType === 'rider' && (
                  <div className="profile-detail-item" data-testid="home-address-display">
                    <span className="profile-detail-label">Home Address</span>
                    <span className="profile-detail-value">{user?.home_address || <em style={{ color: '#999' }}>Not set</em>}</span>
                  </div>
                )}
              </div>

              <div className="profile-actions">
                <button 
                  className="profile-btn primary"
                  onClick={() => setEditing(true)}
                >
                  Edit Profile
                </button>
                <button 
                  className="profile-btn secondary"
                  onClick={() => setShowPasswordForm(!showPasswordForm)}
                >
                  Change Password
                </button>
              </div>
            </>
          )}

          {/* Password Change Form */}
          {showPasswordForm && !editing && (
            <div className="login-form" style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #e2e8f0' }}>
              <div className="input-group">
                <input
                  type="password"
                  name="currentPassword"
                  placeholder="Current Password"
                  value={passwordData.currentPassword}
                  onChange={handlePasswordChange}
                  style={{ marginBottom: '1rem' }}
                />
              </div>
              <div className="input-group">
                <input
                  type="password"
                  name="newPassword"
                  placeholder="New Password (min 6 characters)"
                  value={passwordData.newPassword}
                  onChange={handlePasswordChange}
                  style={{ marginBottom: '1rem' }}
                />
              </div>
              <div className="input-group">
                <input
                  type="password"
                  name="confirmPassword"
                  placeholder="Confirm New Password"
                  value={passwordData.confirmPassword}
                  onChange={handlePasswordChange}
                  style={{ marginBottom: '1.5rem' }}
                />
              </div>
              <div style={{ display: 'flex', gap: '1rem' }}>
                <button 
                  type="button"
                  className="btn-outline"
                  onClick={() => {
                    setShowPasswordForm(false);
                    setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
                  }}
                  disabled={loading}
                  style={{ flex: 1 }}
                >
                  Cancel
                </button>
                <button 
                  type="button"
                  className="login-submit-btn"
                  onClick={handleChangePassword}
                  disabled={loading}
                  style={{ flex: 1 }}
                >
                  {loading ? 'Changing...' : 'Change Password'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default UserProfile;